import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Phone, 
  Users, 
  BarChart3, 
  Headphones, 
  Shield, 
  Zap,
  ArrowRight,
  Check,
  Globe,
  Building2,
  Plug,
  TrendingUp,
  Layers,
  Monitor,
  Handshake,
  Briefcase,
  Store,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { API_URL } from '@/utils/api';
const API = API_URL;

export default function Landing() {
  const [proPrice, setProPrice] = useState(20);
  
  useEffect(() => {
    document.title = "Connect | Cloud PBX Console";
    window.scrollTo(0, 0);
    
    // Fetch pricing from API
    axios.get(`${API}/pricing`)
      .then(res => {
        const plans = res.data?.plans;
        // Check for both new format (retail_price_monthly) and legacy (price_monthly)
        if (plans?.pro?.retail_price_monthly) {
          setProPrice(plans.pro.retail_price_monthly);
        } else if (plans?.pro?.price_monthly) {
          setProPrice(plans.pro.price_monthly);
        }
      })
      .catch(() => {
        // Use default price on error
      });
  }, []);

  const features = [
    {
      icon: Phone,
      title: 'WebRTC Softphone',
      description: 'Make and receive calls directly from your browser with crystal-clear audio quality.'
    },
    {
      icon: Users,
      title: 'Team Presence',
      description: 'See who\'s available, on a call, or away in real-time across your organization.'
    },
    {
      icon: BarChart3,
      title: 'Call Analytics',
      description: 'Comprehensive dashboards showing call volumes, durations, and performance metrics.'
    },
    {
      icon: Headphones,
      title: 'Queue Monitoring',
      description: 'Live view of active calls, waiting callers, and queue statistics.'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with encrypted communications and 99.9% uptime.'
    },
    {
      icon: Zap,
      title: 'Click-to-Call',
      description: 'Initiate calls with a single click from any contact or phone number.'
    }
  ];

  const integrations = [
    { name: 'Hero Internet', status: 'Available', description: 'NZ Cloud PBX' },
    { name: 'More Providers', status: 'Coming Soon', description: 'Additional integrations' }
  ];

  const stats = [
    { value: '99.9%', label: 'Uptime' },
    { value: 'NZ', label: 'Local Support' },
    { value: 'Free', label: 'Tier Available' },
    { value: '50+', label: 'Features' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-slate-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="text-slate-300 hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="text-slate-300 hover:text-white transition-colors">Partners</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                Sign In
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-8">
            <Phone className="w-4 h-4" />
            Cloud PBX Operator Console
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            Your Cloud PBX
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Deserves a Better Console
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            A modern web-based softphone and call management platform that works with your existing Cloud PBX. 
            Make calls, monitor queues, and manage your team — all from your browser.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/pricing">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-lg shadow-xl shadow-cyan-500/25">
                View Plans
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-lg">
                Explore Features
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Integrations Section */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
              <Plug className="w-4 h-4" />
              Works With Your PBX
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Integrates With Your Cloud PBX
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              CONNECT works with leading Cloud PBX providers. Simply connect your existing account and start using a modern operator console today.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 max-w-3xl mx-auto">
            {integrations.map((integration, i) => (
              <div 
                key={i}
                className={`flex-1 p-6 rounded-2xl border ${
                  integration.status === 'Available' 
                    ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-slate-700/50' 
                    : 'bg-slate-800/20 border-slate-700/30 border-dashed'
                }`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    integration.status === 'Available' 
                      ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' 
                      : 'bg-slate-700/30'
                  }`}>
                    <Globe className={`w-6 h-6 ${integration.status === 'Available' ? 'text-orange-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className={`font-bold ${integration.status === 'Available' ? 'text-white' : 'text-slate-400'}`}>
                      {integration.name}
                    </h3>
                    <p className={`text-sm ${integration.status === 'Available' ? 'text-slate-400' : 'text-slate-500'}`}>
                      {integration.description}
                    </p>
                  </div>
                  <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                    integration.status === 'Available' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {integration.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            More provider integrations coming soon.
          </p>
        </div>
      </section>

      {/* Who Is It For - Two Paths */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Who Is CONNECT For?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Whether you're a business looking for a better phone experience, or a partner wanting to offer CONNECT to your clients — we've got you covered.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* For Businesses */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">For Businesses</h3>
                  <p className="text-slate-400">Direct subscription</p>
                </div>
              </div>
              
              <p className="text-slate-300 mb-6 leading-relaxed">
                Get a modern operator console for your team. Subscribe directly and start making calls from your browser in minutes.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'Works with your existing Cloud PBX',
                  'No hardware or plugins required',
                  'Free tier to get started',
                  'Simple per-user pricing'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/business">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-400 hover:to-cyan-500 text-white py-6">
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* For Partners */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                  <Handshake className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">For Partners</h3>
                  <p className="text-slate-400">Partner program</p>
                </div>
              </div>
              
              <p className="text-slate-300 mb-6 leading-relaxed">
                PBX partners, IT consultants, and MSPs can offer CONNECT to their clients with wholesale pricing.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'Recurring revenue on every seat',
                  'NFR subscription available',
                  'Free tier for your customers to trial',
                  'Volume discounts available'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-amber-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link to="/partners">
                <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white py-6">
                  Become a Partner
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to Manage Calls
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              A complete operator console with all the tools your team needs to deliver exceptional service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="group p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/30 hover:bg-slate-800/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-blue-600/30 transition-all">
                  <feature.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                See All Features
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-slate-400 mb-12">
            Pay per user, cancel anytime. Start free with up to 2 users.
          </p>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl border border-slate-700/50 p-8 sm:p-12">
            <div className="flex items-baseline justify-center gap-2 mb-6">
              <span className="text-5xl sm:text-6xl font-bold text-white">${proPrice}</span>
              <span className="text-slate-400 text-lg">/ user / month</span>
            </div>

            <p className="text-slate-400 mb-8">Pro plan pricing. Free tier available for up to 2 users.</p>

            <ul className="space-y-4 mb-8 text-left max-w-sm mx-auto">
              {[
                'Unlimited calls',
                'WebRTC softphone',
                'Call history & recordings',
                'Team presence',
                'Queue monitoring',
                'Analytics dashboard'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-cyan-400" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>

            <Link to="/pricing">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 shadow-xl shadow-cyan-500/25">
                View All Plans
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-y border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Upgrade Your Phone System?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Start with our free tier. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login?tab=register">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">CONNECT</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="hover:text-white transition-colors">Partners</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} Connect-US. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
